import {
  ESFullScreenLoadingIndicator,
  useDocumentTitle,
} from '@energy-stacks/shared';
import { useTranslation } from 'react-i18next';
import { Stack } from '@mui/material';
import { useParams } from 'react-router-dom';
import React, { useCallback, useState } from 'react';
import { useESSnackbar } from '@energy-stacks/core/ui';
import {
  OptimizedTourModel,
  useEditTourDetailsMutation,
} from '@energy-stacks/fleet/feature-tours-data';
import { toursApiErrors } from '../toursApiErrors';
import { TourDetailsEditMode } from './TourDetailsEditMode';
import { EditTourDetailsContextProvider } from './EditTourDetailsContext';
import { TourDetailsEditModeHeader } from './TourDetailsEditModeHeader';
import { TourDetails } from '../create-tour-steps/ReviewStep';
import { SkippedJobsAlert } from '../create-tour-steps/SkippedJobsAlert';
import { useInvalidateJobPool } from '@energy-stacks/fleet/feature-jobs';
import { useUpdateEffect } from 'usehooks-ts';
import { TourJobsTableHeader } from '../tour-details/TourJobsTableHeader';
import { mapEditingTourJobToJobModel } from './mapEditingTourJobToJobModel';
import { JobModel } from '@energy-stacks/fleet/feature-jobs-data';

type EditTourDetailsPageProps = {
  tourDetails: OptimizedTourModel;
  onCancelEditing: () => void;
};

export const EditTourDetailsPage: React.FC<EditTourDetailsPageProps> = ({
  tourDetails,
  onCancelEditing,
}) => {
  const { tourId } = useParams<{ tourId: string }>();
  const [t] = useTranslation('tours');
  useDocumentTitle(t('pageTitle'));
  const [tourDetailsState, setTourDetailsState] = useState<TourDetails>({
    index: 0,
    tour: tourDetails,
    initialTour: tourDetails,
  });
  const { showSnackbar } = useESSnackbar();
  const [editTourDetails, { isLoading: isEditingTour }] =
    useEditTourDetailsMutation();
  const [skippedJobs, setSkippedJobs] = useState<JobModel[]>([]);
  const invalidateJobPool = useInvalidateJobPool();

  useUpdateEffect(() => {
    // Invalidate local state after tour details are re-fetched
    setTourDetailsState({
      index: 0,
      tour: tourDetails,
      initialTour: tourDetails,
    });
  }, [tourDetails]);

  const handleRecalculateTour = useCallback(() => {
    if (!tourDetailsState.tour || !tourId) {
      return;
    }

    const selectedTourJobs = tourDetailsState.tour.tourJobs.jobs;

    editTourDetails({
      body: {
        jobs: selectedTourJobs.map((job) => ({
          jobUID: job.jobId,
          jobPrecedence: job.visitOrder,
        })),
      },
      tourId,
      tourNote: tourDetailsState.tour.note,
    })
      .unwrap()
      .then((response) => {
        const { skippedJobs: skippedJobsResponse } = response;

        if (skippedJobsResponse.length) {
          const skippedJobs = selectedTourJobs.filter((tourJob) =>
            Boolean(
              skippedJobsResponse.find(
                (skippedJob) => skippedJob.jobId === tourJob.jobId
              )
            )
          );

          setSkippedJobs(skippedJobs.map(mapEditingTourJobToJobModel));
          invalidateJobPool();
        } else {
          setSkippedJobs([]);
        }
        showSnackbar('success', 'editTourSuccess', 'tours');
      })
      .catch((error) => {
        const errorCode = toursApiErrors[error.data?.errorCode];
        showSnackbar(
          'error',
          errorCode ? `toursApiErrors.${errorCode}` : undefined,
          'tours'
        );
      });
  }, [
    editTourDetails,
    invalidateJobPool,
    showSnackbar,
    tourDetailsState.tour,
    tourId,
  ]);

  return (
    <EditTourDetailsContextProvider
      config={{
        isEditTourModeActive: true,
        tourDetails: tourDetailsState,
        jobIds: tourDetails
          ? tourDetails.tourJobs.jobs.map((tourJob) => tourJob.jobId)
          : [],
        onCancelEditing,
        onTourDetailsChange: (value) => {
          if (!value) {
            // Should never be undefined
            return;
          }
          setTourDetailsState(value);
        },
      }}
    >
      <Stack sx={{ minHeight: '100%' }} gap={4}>
        <SkippedJobsAlert
          skippedJobs={skippedJobs ?? []}
          totalJobsCount={
            skippedJobs
              ? tourDetails.tourJobs.jobs.length + skippedJobs.length
              : 0
          }
          onDismiss={() => setSkippedJobs([])}
        />
        <Stack direction="column" gap={6}>
          <TourDetailsEditModeHeader
            onRecalculateTour={handleRecalculateTour}
            shouldDisableSave={
              !tourDetailsState ||
              !tourDetailsState.tour.tourJobs.jobs.length ||
              tourDetailsState.tour.tourJobs.jobs.some(
                (job) => job.status === 'CANCELLED' || job.status === 'FAILED'
              )
            }
          />
        </Stack>
        <TourDetailsEditMode
          TourJobsTableHeader={
            <TourJobsTableHeader
              vehicleName={tourDetails.vehicleName}
              vehicleIndex={0}
              startDate={tourDetails.startDate}
            />
          }
        />
      </Stack>
      {isEditingTour ? (
        <ESFullScreenLoadingIndicator text={t('editTourDetailsLoadingText')} />
      ) : null}
    </EditTourDetailsContextProvider>
  );
};
